import React, { useState, useEffect, useCallback } from 'react';
import { Activity, HardDrive, Clock, AlertTriangle, RefreshCw, ShieldCheck, Users } from 'lucide-react';
import api from '../services/api';
import { notificaErrore, messaggioDa } from '../utils/notifiche';
import { dataOraIta } from '../utils/oraItaliana';
import { useAuthStore } from '../store/useStore';
import { stileNuovo } from '../navigazione/paginaIniziale';
import '../styles/StatoSistema.css';
import '../styles/StatoSistemaConsole.css';

/**
 * StatoSistema — «come sta il server», per chi va a guardare.
 *
 * E' la terza faccia della sorveglianza, e le tre non si sostituiscono:
 *   - Healthchecks.io dice **se e' vivo**, da FUORI - e' l'unico che
 *     funziona quando il guasto siamo noi;
 *   - Telegram dice **cosa e' successo**, nel momento in cui succede;
 *   - questa pagina dice **come sta adesso**, con il contorno.
 *
 * Serve soprattutto in due momenti: dopo un allarme, per capire cosa c'e'
 * intorno; e **quando il canale degli allarmi e' rotto** - li' questa e'
 * l'unico posto dove le cose si vedono lo stesso, ed e' il motivo per cui
 * gli allarmi si SCRIVONO nel database prima di provare a mandarli.
 *
 * VIVE SUL SERVER, quindi non si apre a server spento. E' corretto e non
 * e' un limite da correggere: quel caso lo copre Healthchecks. Una
 * schermata che pretendesse di funzionare a server spento sarebbe una
 * bugia.
 *
 * NESSUN DATO DI PAZIENTE, per nessun motivo: qui ci sono numeri, date,
 * nomi di lavori e titoli composti da modelli fissi. Il registro degli
 * errori si mostra per QUANTO PESA, mai per quello che contiene.
 *
 * SOLO ADMIN, e lo decide il server. La voce di menu nascosta non e'
 * protezione: questa pagina dice dove stanno i backup, quanto spazio
 * resta e quando scadono i certificati.
 */

const vicino = (quando) => {
    if (!quando) return '—';
    const ore = (Date.now() - new Date(quando).getTime()) / 3600000;
    if (ore < 1) return `${Math.max(1, Math.round(ore * 60))} min fa`;
    if (ore < 48) return `${Math.round(ore)} ore fa`;
    return `${Math.round(ore / 24)} giorni fa`;
};

const mb = (byte) => (byte === null || byte === undefined ? '—'
    : `${Math.round(Number(byte) / 1048576)} MB`);

const durata = (ms) => {
    if (ms === null || ms === undefined) return '—';
    if (ms < 1000) return `${ms} ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
    return `${Math.round(ms / 60000)} min`;
};

/* Il colore di uno stato di Healthchecks. E' una funzione e non un
   ternario annidato dentro il JSX per due ragioni: si legge, e il
   controllo dei nomi di classe non scambia piu' «new» per una classe -
   cosa che faceva, e aveva ragione lui: in un ternario annidato non si
   distingue una condizione da un nome. */
const classeStato = (stato) => {
    if (stato === 'up') return 'ss-bene';
    if (stato === 'new' || stato === 'paused') return '';
    return 'ss-male';
};

const Riquadro = ({ titolo, Icona, children, nota, classe }) => (
    <section className={`ss-riquadro${classe ? ` ${classe}` : ''}`}>
        <h2 className="ss-riquadro-titolo">{Icona && <Icona size={17} />} {titolo}</h2>
        {nota && <p className="ss-nota">{nota}</p>}
        {children}
    </section>
);

const Voce = ({ etichetta, valore, stato }) => (
    <div className="ss-voce">
        <span className="ss-voce-etichetta">{etichetta}</span>
        <span className={`ss-voce-valore${stato ? ` ss-${stato}` : ''}`}>{valore}</span>
    </div>
);

const StatoSistema = () => {
    const [dati, setDati] = useState(null);
    const [inCorso, setInCorso] = useState(false);
    /* Il totale delle anagrafiche da completare c'e' solo con lo stile nuovo
       (interruttore dello stile, 14/10/2026): con quello di prima il totale sta
       ancora nel riquadro della Dashboard. */
    const conAnagrafiche = stileNuovo(useAuthStore((st) => st.user));

    const carica = useCallback(async () => {
        setInCorso(true);
        try {
            const r = await api.get('/stato-sistema');
            setDati(r.data.data);
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a leggere lo stato del sistema'));
        } finally {
            setInCorso(false);
        }
    }, []);

    useEffect(() => { carica(); }, [carica]);

    if (!dati) {
        return (
            <div className="ss-pagina">
                <h1 className="ss-titolo">Stato del sistema</h1>
                <p className="ss-nota">{inCorso ? 'Sto leggendo…' : 'Nessun dato.'}</p>
            </div>
        );
    }

    const g = dati.gestionale || {};
    const b = dati.backup || {};
    const r = dati.risorse || {};
    const lavori = Array.isArray(dati.lavori) ? dati.lavori : [];
    const sofferenti = lavori.filter((x) => x.in_sofferenza);
    const aperti = (dati.allarmi && dati.allarmi.aperti) || [];

    /* Il backup piu' recente fra registro e disco: il file e' la cosa, la
       riga e' il nostro appunto sulla cosa. */
    const ultimoBackup = (x) => {
        if (!x) return null;
        const a = x.registro ? new Date(x.registro).getTime() : 0;
        const d = x.disco ? x.disco.quando : 0;
        return Math.max(a, d) || null;
    };

    return (
        <div className="ss-pagina">
            <div className="ss-testata">
                <h1 className="ss-titolo">Stato del sistema</h1>
                <button type="button" className="rm-btn" onClick={carica} disabled={inCorso}>
                    <RefreshCw size={14} /> Aggiorna
                </button>
            </div>
            <p className="ss-nota">
                Letto alle {dataOraIta(dati.adesso)}. Questa pagina vive sul server: se il
                server non risponde, non si apre — e quel caso lo sorveglia Healthchecks
                dall'esterno.
            </p>

            {!dati.canale_configurato && (
                <div className="ss-avviso">
                    Il canale degli allarmi <strong>non è configurato</strong>: gli avvisi
                    vengono scritti qui ma non partono. Manca <code>TELEGRAM_CHAT_ID</code>.
                </div>
            )}

            {(aperti.length > 0 || sofferenti.length > 0) && (
                <div className="ss-avviso ss-avviso-grave">
                    {aperti.length > 0 && <>Allarmi ancora aperti: <strong>{aperti.length}</strong>. </>}
                    {sofferenti.length > 0 && <>Lavori in sofferenza: <strong>{sofferenti.length}</strong>.</>}
                </div>
            )}

            <Riquadro titolo="Gestionale" Icona={Activity}>
                <Voce etichetta="Stato" valore={g.stato_pm2 || '—'}
                      stato={g.stato_pm2 === 'online' ? 'bene' : 'male'} />
                <Voce etichetta="Acceso da"
                      valore={g.acceso_da_ms ? durata(g.acceso_da_ms) : '—'} />
                <Voce etichetta="Riavvii da sempre" valore={g.riavvii_totali ?? '—'} />
                <Voce etichetta="Memoria" valore={g.memoria_mb ? `${g.memoria_mb} MB` : '—'} />
                <Voce etichetta="Bundle in linea" valore={g.bundle || '—'} />
                <Voce etichetta="Ultima migration"
                      valore={g.ultima_migration
                          ? `${g.ultima_migration.numero} — ${dataOraIta(g.ultima_migration.applicata_il)}`
                          : '—'} />
            </Riquadro>

            <Riquadro titolo="Backup" Icona={HardDrive}
                      nota="Si guarda il registro dei lavori E il file sul disco: il file è la cosa, la riga è il nostro appunto sulla cosa.">
                <Voce etichetta="Database"
                      valore={ultimoBackup(b.database)
                          ? `${vicino(ultimoBackup(b.database))} · ${b.database.disco_non_leggibile ? 'il file lo controlla il lavoro notturno' : mb(b.database.disco && b.database.disco.byte)}`
                          : 'nessuno'}
                      stato={ultimoBackup(b.database) ? 'bene' : 'male'} />
                <Voce etichetta="Allegati"
                      valore={ultimoBackup(b.allegati)
                          ? `${vicino(ultimoBackup(b.allegati))} · ${b.allegati.disco_non_leggibile ? 'il file lo controlla il lavoro notturno' : mb(b.allegati.disco && b.allegati.disco.byte)}`
                          : 'nessuno'}
                      stato={ultimoBackup(b.allegati) ? 'bene' : 'male'} />
                <Voce etichetta="Ultimo ripristino verificato"
                      valore={b.ultimo_ripristino_verificato
                          ? vicino(b.ultimo_ripristino_verificato) : 'mai'}
                      stato={b.ultimo_ripristino_verificato ? 'bene' : 'male'} />
            </Riquadro>

            <Riquadro titolo="Lavori automatici" Icona={Clock}
                      nota="Il rosso resta rosso finché il lavoro non riesce di nuovo: un guasto che sparisce perché il lavoro ha smesso di girare è il contrario di quello che serve.">
                <div className="ss-tabella-fuori">
                    <table className="ss-tabella">
                        <thead>
                            <tr><th>Lavoro</th><th>Ultima</th><th>Esito</th><th>Durata</th></tr>
                        </thead>
                        <tbody>
                            {lavori.map((x) => (
                                <tr key={x.nome} className={x.in_sofferenza ? 'ss-riga-male' : ''}>
                                    <td>{x.nome}</td>
                                    <td>{vicino(x.iniziato_il)}</td>
                                    <td className={x.esito === 'ok' ? 'ss-bene' : 'ss-male'}>
                                        {x.esito}
                                        {x.messaggio && <span className="ss-motivo">{x.messaggio}</span>}
                                    </td>
                                    <td>{durata(x.durata_ms)}</td>
                                </tr>
                            ))}
                            {!lavori.length && (
                                <tr><td colSpan={4}>Nessun lavoro ha ancora lasciato una riga.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Riquadro>

            <Riquadro titolo="Risorse" Icona={HardDrive}>
                <Voce etichetta="Disco usato"
                      valore={r.disco ? `${r.disco.uso_percento}% · ${r.disco.libero_gb} GB liberi` : '—'}
                      stato={r.disco && r.disco.uso_percento >= 85 ? 'male' : 'bene'} />
                <Voce etichetta="Database" valore={mb(r.database_byte)} />
                <Voce etichetta="Registro modifiche"
                      valore={`${mb(r.registro_byte)} · ${Number(r.registro_righe || 0).toLocaleString('it-IT')} righe`} />
                <Voce etichetta="Registro errori (quanto pesa)"
                      valore={r.log_errori
                          ? `${mb(r.log_errori.byte)} · ultima scrittura ${vicino(r.log_errori.ultima_scrittura)}`
                          : '—'} />
            </Riquadro>

            <Riquadro titolo="Scadenze" Icona={ShieldCheck}>
                {(dati.scadenze || []).map((s) => (
                    <Voce key={s.cosa} etichetta={s.cosa}
                          valore={s.giorni === undefined || s.giorni === null ? 'non leggibile'
                              : `fra ${s.giorni} giorni (${dataOraIta(s.scade_il)})`}
                          stato={s.giorni !== undefined && s.giorni !== null && s.giorni <= 14 ? 'male' : 'bene'} />
                ))}
            </Riquadro>

            {conAnagrafiche && (
                <Riquadro titolo="Anagrafiche" Icona={Users} classe="ss-riquadro--anagrafiche"
                          nota="Il numero scende man mano che la segreteria le completa all'arrivo dei pazienti: dice quanto del vecchio archivio resta da verificare.">
                    <Voce etichetta="Da completare (tutto l'archivio)"
                          valore={dati.anagrafiche && Number.isInteger(dati.anagrafiche.da_completare)
                              ? dati.anagrafiche.da_completare.toLocaleString('it-IT') : 'non leggibile'} />
                </Riquadro>
            )}

            <Riquadro titolo="Controlli esterni (Healthchecks)" Icona={ShieldCheck}
                      nota="Chiedono da fuori se il server è vivo. Sono l'unica parte che funziona quando il guasto siamo noi.">
                {Array.isArray(dati.healthchecks) ? (
                    <div className="ss-tabella-fuori">
                        <table className="ss-tabella">
                            <thead><tr><th>Controllo</th><th>Stato</th><th>Ultimo battito</th></tr></thead>
                            <tbody>
                                {dati.healthchecks.map((c) => (
                                    <tr key={c.slug}>
                                        <td>{c.nome}</td>
                                        <td className={classeStato(c.stato)}>{c.stato}</td>
                                        <td>{c.ultimo_battito ? vicino(c.ultimo_battito) : 'mai'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="ss-nota">Non configurati, o non raggiungibili adesso.</p>
                )}
            </Riquadro>

            <Riquadro titolo="Ultimi allarmi" Icona={AlertTriangle}
                      nota="Vengono scritti prima di essere mandati: se il canale è rotto, si vedono comunque qui.">
                <div className="ss-tabella-fuori">
                    <table className="ss-tabella">
                        <thead><tr><th>Quando</th><th>Livello</th><th>Cosa</th><th>Mandato</th></tr></thead>
                        <tbody>
                            {((dati.allarmi && dati.allarmi.ultimi) || []).map((a) => (
                                <tr key={a.id}>
                                    <td>{vicino(a.creato_il)}</td>
                                    <td className={a.livello === 'critico' ? 'ss-male'
                                        : a.livello === 'attenzione' ? 'ss-attenzione' : ''}>
                                        {a.livello}
                                    </td>
                                    <td>
                                        {a.titolo}
                                        {a.tipo !== 'allarme' && <span className="ss-motivo">{a.tipo}</span>}
                                    </td>
                                    <td className={a.consegnato_il ? 'ss-bene' : 'ss-male'}>
                                        {a.consegnato_il ? 'sì' : 'no'}
                                        {!a.consegnato_il && a.ultimo_errore
                                            && <span className="ss-motivo">{a.ultimo_errore}</span>}
                                    </td>
                                </tr>
                            ))}
                            {!((dati.allarmi && dati.allarmi.ultimi) || []).length && (
                                <tr><td colSpan={4}>Nessun allarme finora.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Riquadro>
        </div>
    );
};

export default StatoSistema;
